import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../Icon';
import ArchivedToggle from '../ui/ArchivedToggle';
import FilterPopover, { FilterSelectSpec } from '../ui/FilterPopover';
import ExportButton from './ExportButton';
import ShipmentsViewTabs from '../procurement/ShipmentsViewTabs';
import { useRole } from '../../hooks/useRole';
import {
  PROC_EVENTS, setAgentsBar, setInventoryBar, setOrdersBar, setShipmentsBar, useProcurementBar,
} from '../../hooks/useProcurementBar';
import { ORDER_STATUS_OPTIONS, SHIPMENT_LEG_OPTIONS, SHIPMENT_MODE_OPTIONS, SHIPMENT_STATUS_OPTIONS } from '../procurement/procurementLabels';

/**
 * Breadcrumb actions of the Procurement screens. Every page action lives here
 * (not in a toolbar inside the page): the Archived switch, one Filters popover,
 * the primary create button, an icon-only Export. Text search is the header
 * search the Header hands in. Write buttons only show for planner level and up.
 */

const send = (name: string) => window.dispatchEvent(new CustomEvent(name));

const select = (
  key: string, label: string, allLabel: string, options: Array<{ value: string; label: string }>,
  value: string, onChange: (v: string) => void,
): FilterSelectSpec => ({
  key, label, options: [{ value: '', label: allLabel }, ...options], value, defaultValue: '', onChange,
});

const PAYMENT_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Part paid' },
  { value: 'paid', label: 'Paid' },
];
const RECEIVING_OPTIONS = [
  { value: 'none', label: 'Nothing received' },
  { value: 'partial', label: 'Part received' },
  { value: 'complete', label: 'Fully received' },
];

export const OrdersListBar: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canViewFinancials } = useRole();
  const { orders } = useProcurementBar();
  const selects = [
    select('status', 'Status', 'All statuses', ORDER_STATUS_OPTIONS, orders.status, status => setOrdersBar({ status })),
    select('receiving', 'Receiving', 'Any receiving', RECEIVING_OPTIONS, orders.receiving, receiving => setOrdersBar({ receiving })),
    // Payment status is money: a contractor does not get the filter.
    ...(canViewFinancials ? [select('payment', 'Payment', 'Any payment status', PAYMENT_OPTIONS, orders.payment, payment => setOrdersBar({ payment }))] : []),
  ];
  return (
    <>
      <div className="header-filter-group">
        <ArchivedToggle checked={orders.archived} onChange={archived => setOrdersBar({ archived })} />
      </div>
      <FilterPopover selects={selects} />
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => navigate('/procurement/orders/new')} title="New Order">
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New Order</span>
        </button>
      )}
      <ExportButton exportKey="procurement-orders" />
    </>
  );
};

export const OrderDetailBar: React.FC<{ isNew?: boolean }> = ({ isNew }) => {
  const navigate = useNavigate();
  const { canEdit } = useRole();
  const { detail } = useProcurementBar();
  const archived = detail?.kind === 'order' && detail.archived;
  return (
    <>
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => send(PROC_EVENTS.orderSave)} title="Save">
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">Save</span>
        </button>
      )}
      {canEdit && !isNew && !archived && (
        <button type="button" className="header-action-btn header-action-btn--iconic" onClick={() => send(PROC_EVENTS.orderReceive)} title="Receive goods against this order">
          <Icon name="package" size={16} />
          <span className="header-action-btn__label">Receive Goods</span>
        </button>
      )}
      <button type="button" className="header-action-btn" onClick={() => navigate('/procurement/orders')}>
        {canEdit ? 'Cancel' : 'Back'}
      </button>
      {canEdit && !isNew && (
        archived ? (
          <button type="button" className="header-action-btn" onClick={() => send(PROC_EVENTS.orderRestore)} title="Restore this order">
            Restore
          </button>
        ) : (
          <button type="button" className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={() => send(PROC_EVENTS.orderArchive)} title="Archive this order">
            <Icon name="trash" size={16} />
            <span className="header-action-btn__label">Archive</span>
          </button>
        )
      )}
    </>
  );
};

export const ShipmentsListBar: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit } = useRole();
  const { shipments } = useProcurementBar();
  const selects = [
    select('status', 'Status', 'All statuses', SHIPMENT_STATUS_OPTIONS, shipments.status, status => setShipmentsBar({ status })),
    select('leg', 'Leg', 'All legs', SHIPMENT_LEG_OPTIONS, shipments.leg, leg => setShipmentsBar({ leg })),
    select('mode', 'Mode', 'All modes', SHIPMENT_MODE_OPTIONS, shipments.mode, mode => setShipmentsBar({ mode })),
  ];
  return (
    <>
      <ShipmentsViewTabs view={shipments.view} readyCount={shipments.readyCount} onChange={view => setShipmentsBar({ view })} />
      <FilterPopover selects={selects} />
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => navigate('/procurement/shipments/new')} title="New Shipment">
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New Shipment</span>
        </button>
      )}
      <ExportButton exportKey="procurement-shipments" />
    </>
  );
};

export const ShipmentDetailBar: React.FC<{ isNew?: boolean }> = ({ isNew }) => {
  const navigate = useNavigate();
  const { canEdit } = useRole();
  const { detail } = useProcurementBar();
  const cancelled = detail?.kind === 'shipment' && detail.archived;
  return (
    <>
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => send(PROC_EVENTS.shipmentSave)} title="Save">
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">Save</span>
        </button>
      )}
      <button type="button" className="header-action-btn" onClick={() => navigate('/procurement/shipments')}>
        {canEdit ? 'Cancel' : 'Back'}
      </button>
      {canEdit && !isNew && detail?.kind === 'shipment' && detail.status === 'draft' && (
        <button type="button" className="header-action-btn header-action-btn--iconic" onClick={() => send(PROC_EVENTS.shipmentActivate)} title="Book this shipment (draft to active)">
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">Activate</span>
        </button>
      )}
      {canEdit && !isNew && (
        cancelled ? (
          <button type="button" className="header-action-btn" onClick={() => send(PROC_EVENTS.shipmentReactivate)} title="Move this shipment back to draft">
            Reactivate
          </button>
        ) : (
          <button type="button" className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={() => send(PROC_EVENTS.shipmentCancel)} title="Cancel this shipment">
            <Icon name="trash" size={16} />
            <span className="header-action-btn__label">Cancel Shipment</span>
          </button>
        )
      )}
    </>
  );
};

export const AgentsListBar: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit } = useRole();
  const { agents } = useProcurementBar();
  return (
    <>
      <div className="header-filter-group">
        <ArchivedToggle label="Inactive" checked={agents.showInactive} onChange={showInactive => setAgentsBar({ showInactive })} />
      </div>
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => navigate('/procurement/shipping-agents/new')} title="New Agent">
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New Agent</span>
        </button>
      )}
      <ExportButton exportKey="shipping" />
    </>
  );
};

export const InventoryBar: React.FC = () => {
  const { canEdit } = useRole();
  const { inventory } = useProcurementBar();
  const selects = inventory.itemTypes.length > 0
    ? [select('type', 'Type', 'All types', inventory.itemTypes.map(t => ({ value: t, label: t })), inventory.itemType, itemType => setInventoryBar({ itemType }))]
    : [];
  return (
    <>
      <div className="header-filter-group">
        <ArchivedToggle checked={inventory.archived} onChange={archived => setInventoryBar({ archived })} />
      </div>
      {selects.length > 0 && <FilterPopover selects={selects} />}
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => send(PROC_EVENTS.inventoryAdd)} title="New Item">
          <Icon name="plus" size={16} />
          <span className="header-action-btn__label">New Item</span>
        </button>
      )}
      <ExportButton exportKey="inventory" />
    </>
  );
};

/** Save, Cancel and Archive/Restore/Delete of one shipping agent (the page listens for these window events). */
export const AgentDetailBar: React.FC<{ isNew?: boolean; active?: boolean }> = ({ isNew, active = true }) => {
  const { canEdit } = useRole();
  return (
    <>
      {canEdit && (
        <button type="button" className="header-action-btn header-action-btn--iconic primary" onClick={() => send('shipping-agent-save')} title="Save">
          <Icon name="check" size={16} />
          <span className="header-action-btn__label">Save</span>
        </button>
      )}
      <button type="button" className="header-action-btn" onClick={() => send('shipping-agent-cancel')}>
        {canEdit ? 'Cancel' : 'Back'}
      </button>
      {canEdit && !isNew && (
        <>
          <div className="header-divider" />
          {active ? (
            <button type="button" className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={() => send('shipping-agent-archive')} title="Archive this agent">
              <Icon name="trash" size={16} />
              <span className="header-action-btn__label">Archive</span>
            </button>
          ) : (
            <>
              <button type="button" className="header-action-btn" onClick={() => send('shipping-agent-restore')} title="Restore this agent">Restore</button>
              <button type="button" className="header-action-btn header-action-btn--iconic header-btn-danger" onClick={() => send('shipping-agent-delete')} title="Permanently delete this agent">
                <Icon name="trash" size={16} />
                <span className="header-action-btn__label">Delete</span>
              </button>
            </>
          )}
        </>
      )}
    </>
  );
};
